import {
  CENTER_X,
  CENTER_Y,
  DROP_TAP_RADIUS,
  R_PLANET,
  angleDist,
  bearingOf,
  radiusOf,
  slotRadius,
  slotTheta,
  type Command,
  type Input,
  type World,
} from '@playnets/sim';

/**
 * The ring turns on its own, so this handles taps and nothing else.
 *
 * A drag is still tracked, but only to know it *wasn't* a tap — dragging across
 * the board while meaning to tap a mount shouldn't build something, and a
 * scrolling finger shouldn't collect juice it happened to pass over.
 */
const TAP_MOVE_UNITS = 14;
const TAP_TIME_MS = 380;
/** Drop radius for drag-to-place, in playfield units. */
const DROP_TARGET_RADIUS = 62;

export interface InputHooks {
  /** A tap that landed on a mount. */
  onSlotTap: (slotId: number) => void;
  /** A tap that landed on the planet body itself — opens its upgrades. */
  onPlanetTap: () => void;
  /** A tap that landed on nothing. Closes the build sheet. */
  onEmptyTap: () => void;
  /** A drag started — used to dismiss the sheet on the first swipe. */
  onDragStart: () => void;
}

export class InputController {
  private commands: Command[] = [];

  private down = false;
  private dragging = false;
  private startX = 0;
  private startY = 0;
  private startTime = 0;

  constructor(
    private readonly world: () => World,
    private readonly toLogical: (x: number, y: number) => { x: number; y: number },
    private readonly hooks: InputHooks,
    /** Does this mount have anything to offer at all? */
    private readonly hasOptions: (slotId: number) => boolean,
    /** What the player is carrying off the build bar, if anything. */
    private readonly carrying: () => string | null,
    /** Would that card do something on this mount — build or merge? */
    private readonly accepts: (slotId: number, structureId: string) => boolean,
  ) {}

  attach(el: HTMLElement): () => void {
    const down = (ev: PointerEvent) => this.pointerDown(ev);
    const move = (ev: PointerEvent) => this.pointerMove(ev);
    const up = (ev: PointerEvent) => this.pointerUp(ev);

    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('contextmenu', preventDefault);
    el.addEventListener('dragstart', preventDefault);
    el.addEventListener('selectstart', preventDefault);

    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('contextmenu', preventDefault);
      el.removeEventListener('dragstart', preventDefault);
      el.removeEventListener('selectstart', preventDefault);
    };
  }

  private pointerDown(ev: PointerEvent): void {
    // No two-finger gestures (§9). A second pointer is ignored outright.
    if (this.down) return;
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    const p = this.toLogical(ev.clientX, ev.clientY);
    this.down = true;
    this.dragging = false;
    this.startX = p.x;
    this.startY = p.y;
    this.startTime = ev.timeStamp;
  }

  private pointerMove(ev: PointerEvent): void {
    if (!this.down || this.dragging) return;
    const p = this.toLogical(ev.clientX, ev.clientY);
    if (Math.hypot(p.x - this.startX, p.y - this.startY) < TAP_MOVE_UNITS) return;
    this.dragging = true;
    this.hooks.onDragStart();
  }

  private pointerUp(ev: PointerEvent): void {
    if (!this.down) return;
    this.down = false;
    if (this.dragging) return;
    if (ev.timeStamp - this.startTime <= TAP_TIME_MS) this.tap();
  }

  private tap(): void {
    const bearing = bearingOf(this.startX, this.startY);
    const r = radiusOf(this.startX, this.startY);

    // Carrying a card widens every empty mount to the drop radius, and beats
    // everything else on the board. Aiming a card you're holding is the same
    // problem as aiming one you're dragging, so it gets the same target size —
    // otherwise a near miss silently puts the card back and reads as the tap
    // not registering.
    const carried = this.carrying();
    if (carried !== null) {
      const target = this.dropTarget(this.startX, this.startY, carried);
      if (target !== null) {
        this.hooks.onSlotTap(target);
        return;
      }
    }

    // Juice and mounts share the playfield, so nearest wins rather than one
    // category always beating the other. Mounts sit still and can be tapped
    // again in a second; juice are on a timer, so their target is fatter.
    const slot = this.hitSlot(this.startX, this.startY);
    const juice = this.hitDrop(bearing, r);

    if (juice !== null && (slot === null || juice.dist < slot.dist)) {
      this.commands.push({ kind: 'collect', theta: bearing, r });
      return;
    }
    if (slot !== null) {
      this.hooks.onSlotTap(slot.id);
      return;
    }

    // Inside the crust, clear of the mounts: that's the planet, and tapping it
    // is how you upgrade the planet.
    if (r <= R_PLANET - 24) {
      this.hooks.onPlanetTap();
      return;
    }

    this.hooks.onEmptyTap();
  }

  /** Nearest juice within the tap radius, in the same units as `hitSlot`. */
  private hitDrop(theta: number, r: number): { id: number; dist: number } | null {
    const world = this.world();
    let best: { id: number; dist: number } | null = null;
    for (const d of world.drops) {
      if (!d.alive || d.collected) continue;
      const dr = d.r - r;
      const dTheta = angleDist(d.theta, theta) * ((d.r + r) * 0.5);
      const dist = Math.hypot(dr, dTheta);
      if (dist <= DROP_TAP_RADIUS && (!best || dist < best.dist)) {
        best = { id: d.id, dist };
      }
    }
    return best;
  }

  /** Nearest mount within a comfortable finger radius. 44 units minimum (§9). */
  private hitSlot(x: number, y: number): { id: number; dist: number } | null {
    const world = this.world();
    let best: { id: number; dist: number } | null = null;
    let bestDist = 30;
    for (const slot of world.slots) {
      // Dormant mounts — nothing this level lets you put there — aren't
      // tappable, so a tap near one falls through to the planet or the ring.
      if (!slot.structure && !this.hasOptions(slot.id)) continue;
      const theta = slotTheta(world, slot);
      const base = slotRadius(world, slot);
      const sx = CENTER_X + Math.cos(theta) * base;
      const sy = CENTER_Y + Math.sin(theta) * base;
      const d = Math.hypot(sx - x, sy - y);
      if (d < bestDist) {
        bestDist = d;
        best = { id: slot.id, dist: d };
      }
    }
    return best;
  }

  /**
   * Nearest mount the carried card would do something to, for drag-to-place.
   *
   * Deliberately more generous than `hitSlot`: a tap is aimed with a fingertip
   * you can see past, but a drag is aimed under a card your thumb is already
   * covering. Mounts the card has no business on are skipped rather than
   * merely rejected on release — letting them compete for the drop would make
   * the one next to them harder to hit.
   */
  dropTargetAt(clientX: number, clientY: number, structureId: string): number | null {
    const p = this.toLogical(clientX, clientY);
    return this.dropTarget(p.x, p.y, structureId);
  }

  /** Same test, in playfield units. */
  private dropTarget(x: number, y: number, structureId: string): number | null {
    const world = this.world();
    let best: number | null = null;
    let bestDist = DROP_TARGET_RADIUS;
    for (const slot of world.slots) {
      if (!this.accepts(slot.id, structureId)) continue;
      const theta = slotTheta(world, slot);
      const base = slotRadius(world, slot);
      const d = Math.hypot(
        CENTER_X + Math.cos(theta) * base - x,
        CENTER_Y + Math.sin(theta) * base - y,
      );
      if (d < bestDist) {
        bestDist = d;
        best = slot.id;
      }
    }
    return best;
  }

  /** Queue a command from the React layer (build, upgrade, ultimate…). */
  push(cmd: Command): void {
    this.commands.push(cmd);
  }

  /** Hand the accumulated frame of input to the sim and reset. */
  consume(): Input {
    const input: Input = { commands: this.commands };
    this.commands = [];
    return input;
  }

  get isDragging(): boolean {
    return this.dragging;
  }
}

function preventDefault(ev: Event): void {
  ev.preventDefault();
}
